// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Single source of truth for the icon-rail items, per persona, so the shell
// stays consistent. Section tab bars declare their own tabs via PageTabs.

import type { ComponentType } from 'react';
import {
  Home,
  Grid,
  Analytics,
  License,
  Upload,
  Receipt,
  UserMultiple,
  ChartBar,
  Security,
  Tools,
  DocumentTasks,
} from '@carbon/icons-react';
import type { Persona } from '../../stores/shellStore';
import { DISPLAY_LOCAL_ONLY } from '../../config/featureFlags';

export type Section =
  | 'home'
  | 'site'
  | 'log'
  | 'audit'
  | 'records'
  | 'fleet'
  | 'optimization'
  | 'onboarding'
  | 'bills'
  | 'users'
  | 'access'
  | 'executive'
  | 'workOrders'
  | 'sop';

export interface RailItem {
  section: Section;
  label: string;
  icon: ComponentType<{ size?: number }>;
  to: string;
  /**
   * Route prefix(es) used to compute the active state. A list matches the item
   * as active when the pathname is under any of the prefixes (e.g. the combined
   * Notes/Audit item highlights on both `/log` and `/audit`).
   */
  match: string | string[];
  /** Requires admin (on top of the Manager persona). */
  adminOnly?: boolean;
}

/**
 * Manager rail sections hidden unless DISPLAY_LOCAL_ONLY is on — surfaces
 * that aren't ready to be shown outside the team. The matching routes are
 * redirected in AppRoutes.tsx, so dropping the rail item here isn't the only
 * thing standing between a viewer and the page.
 *
 * Keep this list and LOCAL_ONLY_ROUTES (AppRoutes.tsx) in sync.
 */
const LOCAL_ONLY_SECTIONS: ReadonlySet<Section> = new Set<Section>([
  'sop',
  'onboarding',
  'bills',
  'users',
]);

/** Drop local-only rail items unless the flag opts them back in. */
function visibleItems(items: RailItem[]): RailItem[] {
  if (DISPLAY_LOCAL_ONLY) return items;
  return items.filter(item => !LOCAL_ONLY_SECTIONS.has(item.section));
}

/**
 * Where the Site rail item, `/site`, and the legacy `/site/:id/asset-topology`
 * bookmark land: Power Flow, for every persona. The page is not gated — an
 * operator reads the one-line as much as a manager does (PR #420 review).
 */
export const siteLandingPath = (siteId: string): string => `/site/${siteId}/power-flow`;

/**
 * SOP binding coverage for the selected site. The design binding's `goSop`
 * scopes to a site and drills in (`siteFilter: fleetSite, siteDrilled: true`),
 * so this is a site screen reached from the rail — not a fleet tab. The site is
 * shell state rather than a route param, so the path carries no site id.
 *
 * Ops Manager only, matching the binding, which carries this in the `isManager`
 * rail block (between Optimization and Onboarding) and not in `isOperator`.
 * Fleet-wide binding compliance is a manager surface; an operator reaches the
 * procedure for the asset in front of them through Site › Procedures.
 */
const SOP_GOVERNANCE: RailItem = {
  section: 'sop',
  label: 'SOP governance',
  icon: DocumentTasks,
  to: '/sop-governance',
  match: '/sop-governance',
};

/**
 * Icon rail for a persona. `siteId` scopes the Site link. When `isFleet` is
 * true (manager viewing the whole fleet) the rail shows the **Fleet** item in
 * place of **Site**; a specific site selection shows Site.
 */
export function railItemsFor(persona: Persona, siteId: string, isFleet = false): RailItem[] {
  const siteOrFleet: RailItem = isFleet
    ? { section: 'fleet', label: 'Fleet', icon: ChartBar, to: '/fleet', match: '/fleet' }
    : {
        section: 'site',
        label: 'Site',
        icon: Grid,
        to: siteLandingPath(siteId),
        match: '/site',
      };
  if (persona === 'executive') {
    // Executives are read-only / posture-only, so admin-only surfaces like
    // Bills-adjacent management screens don't apply here beyond what's listed.
    return [
      {
        section: 'executive',
        label: 'Fleet posture',
        icon: ChartBar,
        to: '/executive',
        match: '/executive',
      },
      { section: 'bills', label: 'Bills', icon: Receipt, to: '/bills', match: '/bills' },
      {
        section: 'access',
        label: 'Access & permissions',
        icon: Security,
        to: '/access',
        match: '/access',
      },
    ];
  }

  if (persona === 'manager') {
    return visibleItems([
      { section: 'home', label: 'Home', icon: Home, to: '/home', match: '/home' },
      ...(isFleet ? [] : [siteOrFleet]),
      {
        section: 'optimization',
        label: 'Recommendations',
        icon: Analytics,
        to: '/recommendations',
        match: '/recommendations',
      },
      SOP_GOVERNANCE,
      { section: 'records', label: 'Notes', icon: License, to: '/log', match: ['/log', '/audit'] },
      {
        section: 'workOrders',
        label: 'Work orders',
        icon: Tools,
        to: '/work-orders',
        match: '/work-orders',
      },
      {
        section: 'onboarding',
        label: 'Onboarding',
        icon: Upload,
        to: '/onboarding',
        match: '/onboarding',
      },
      { section: 'bills', label: 'Bills', icon: Receipt, to: '/bills', match: '/bills' },
      {
        section: 'users',
        label: 'User management',
        icon: UserMultiple,
        to: '/users',
        match: '/users',
      },
    ]);
  }

  // operator
  return [
    { section: 'home', label: 'Home', icon: Home, to: '/home', match: '/home' },
    siteOrFleet,
    {
      section: 'workOrders',
      label: 'Work orders',
      icon: Tools,
      to: '/work-orders',
      match: '/work-orders',
    },
    { section: 'records', label: 'Notes', icon: License, to: '/log', match: ['/log', '/audit'] },
  ];
}
